import React, { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { RANKS } from "@/data/gameData";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";

const MAP_LORE = [
  "Where every great voyage begins! Old sailors gather here to share tales and verses passed down through generations.",
  "Named after Robinson Crusoe! On this deserted isle, you'll find treasures of self-reliance and adventure among the palm trees.",
  "Coral Bay sparkles with beauty! Divers search the reef for pearls of wisdom hidden among the colorful coral and sea creatures.",
  "Inspired by Homer's great epic! Here the sirens sing beautiful poetry, and ancient Greek columns echo with timeless words.",
  "Robin Hood's legendary wood! Under great oak trees, merry folk gather to share ballads and stories of courage and honor.",
  "Beyond the wardrobe lies a land of wonder! Every snowflake carries a verse, every lamppost lights a new stanza.",
  "X marks the spot! The greatest treasures here aren't gold doubloons, but the riches of a mind full of memorized verse.",
  "Command the Golden Fleet! A magnificent armada of ships, each one carrying a library of treasured verse across the seven seas.",
  "The final destination! The Great Library of the Ages, where every poem and song ever memorized is preserved forever.",
];

const NODE_COLORS = ["#92400E", "#38BDF8", "#8B5CF6", "#22C55E", "#FFC107", "#FB7185", "#D97706", "#0EA5E9", "#7C3AED"];

// Node positions - zigzag pattern like Duolingo, vertical scroll
// x is percentage from left (20-80%), y increases downward
const NODE_POSITIONS = [
  { x: 50, label: "start" },
  { x: 72 },
  { x: 30 },
  { x: 68 },
  { x: 35 },
  { x: 70 },
  { x: 32 },
  { x: 65 },
  { x: 50, label: "end" },
];

export default function TreasureMap({ stats: propStats }) {
  const { currentUser } = useContext(UserContext);
  const [stats, setStats] = useState(propStats);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    if (propStats) { setStats(propStats); return; }
    if (currentUser) {
      axios.get(`${API}/users/${currentUser.id}/stats`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [currentUser, propStats]);

  if (!stats) return <div className="text-center py-20 font-heading" style={{ color: "#7A5946" }}>Loading map...</div>;

  const currentRank = stats.rank?.rank || 1;
  const nodeSpacing = 110; // px between nodes vertically
  const nodeSize = 60;
  const currentNodeSize = 72;
  const mapHeight = (RANKS.length - 1) * nodeSpacing + 140;

  return (
    <div className="slide-up" data-testid="treasure-map-page">
      <h2 className="font-heading text-2xl font-bold mb-1" style={{ color: "#4A3224" }}>Treasure Map</h2>
      <p className="text-sm mb-4 leading-relaxed" style={{ color: "#7A5946" }}>Memorize new items to earn gold coins for the store. Review what you've learned to climb the ranks and unlock new treasures along the way!</p>

      {/* Parchment map container */}
      <div className="rounded-2xl overflow-hidden relative"
        style={{
          background: "linear-gradient(180deg, #F5DEB3 0%, #EEDCBE 20%, #F5DEB3 40%, #E8D5B0 60%, #F5DEB3 80%, #EEDCBE 100%)",
          border: "4px solid #8C5E3C",
          boxShadow: "0 8px 0 #5C3D2E, inset 0 0 50px rgba(139,90,43,0.1)",
          padding: "24px 8px",
          minHeight: mapHeight
        }}
        data-testid="map-container">

        {/* Subtle map texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(139,90,43,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(139,90,43,0.3) 0%, transparent 50%)"
        }} />

        {/* Compass rose - top right */}
        <div className="absolute top-4 right-4 opacity-25">
          <svg width="36" height="36" viewBox="0 0 60 60">
            <circle cx="30" cy="30" r="26" fill="none" stroke="#8B6914" strokeWidth="1.5" />
            <path d="M30 4 L33 28 L30 30 L27 28 Z" fill="#8B6914" />
            <path d="M30 56 L33 32 L30 30 L27 32 Z" fill="#8B6914" opacity="0.4" />
            <path d="M4 30 L28 27 L30 30 L28 33 Z" fill="#8B6914" opacity="0.5" />
            <path d="M56 30 L32 27 L30 30 L32 33 Z" fill="#8B6914" opacity="0.5" />
            <text x="30" y="14" textAnchor="middle" fontSize="7" fill="#8B6914" fontWeight="bold">N</text>
          </svg>
        </div>

        {/* SVG Paths connecting nodes + hand-drawn geographic details */}
        <svg className="absolute inset-0 w-full" style={{ height: mapHeight }} viewBox={`0 0 100 ${mapHeight}`} preserveAspectRatio="none">
          
          {/* ── Hand-drawn geographic details ── */}
          
          {/* Waves near The Docks (top) */}
          <path d="M6 30 Q11 25 16 30 Q21 35 26 30 Q31 25 36 30" fill="none" stroke="#5C7A8A" strokeWidth="0.5" opacity="0.25" />
          <path d="M3 38 Q8 33 13 38 Q18 43 23 38" fill="none" stroke="#5C7A8A" strokeWidth="0.4" opacity="0.20" />
          <path d="M76 50 Q81 45 86 50 Q91 55 96 50" fill="none" stroke="#5C7A8A" strokeWidth="0.4" opacity="0.20" />
          <path d="M82 58 Q86 54 90 58" fill="none" stroke="#5C7A8A" strokeWidth="0.3" opacity="0.15" />

          {/* Tiny ship near Crusoe's Island */}
          <g transform="translate(87, 125)" opacity="0.30">
            <path d="M-5 1 L5 1 L4 4 L-4 4 Z" fill="#6B4226" />
            <line x1="0" y1="-6" x2="0" y2="1" stroke="#6B4226" strokeWidth="0.5" />
            <path d="M0 -6 L4 -3.5 L0 -1.5 Z" fill="#D2B48C" opacity="0.8" />
            <path d="M-6 4 Q0 6 6 4" fill="none" stroke="#5C7A8A" strokeWidth="0.4" />
          </g>

          {/* Palm trees near Crusoe's Island */}
          <g transform="translate(80, 105)" opacity="0.28">
            <line x1="0" y1="0" x2="0.5" y2="10" stroke="#6B4226" strokeWidth="0.6" />
            <path d="M0.5 0 Q-5 -2 -7 2" fill="none" stroke="#2D6016" strokeWidth="0.7" />
            <path d="M0.5 0 Q5 -3 7 1" fill="none" stroke="#2D6016" strokeWidth="0.7" />
            <path d="M0.5 0 Q-2 -5 0 -6" fill="none" stroke="#2D6016" strokeWidth="0.6" />
            <path d="M0.5 0 Q3 -4 5 -3" fill="none" stroke="#3A7020" strokeWidth="0.5" />
          </g>
          <g transform="translate(85, 112)" opacity="0.22">
            <line x1="0" y1="0" x2="0" y2="7" stroke="#6B4226" strokeWidth="0.5" />
            <path d="M0 0 Q-4 -2 -6 1" fill="none" stroke="#2D6016" strokeWidth="0.6" />
            <path d="M0 0 Q4 -2 5 1" fill="none" stroke="#3A7020" strokeWidth="0.6" />
          </g>

          {/* Small island with sand */}
          <ellipse cx="12" cy="148" rx="6" ry="2" fill="#C4A46C" opacity="0.18" />
          <ellipse cx="12" cy="148" rx="6" ry="2" fill="none" stroke="#8B6914" strokeWidth="0.3" opacity="0.15" />

          {/* Mountains near Odyssey Cove */}
          <g opacity="0.28">
            <path d="M80 240 L85 222 L90 240" fill="#A09070" opacity="0.3" stroke="#6B5030" strokeWidth="0.4" />
            <path d="M87 240 L93 220 L99 240" fill="#A09070" opacity="0.25" stroke="#6B5030" strokeWidth="0.4" />
            <path d="M85 222 L83.5 226 L86.5 226 Z" fill="white" opacity="0.5" />
            <path d="M93 220 L91 224 L95 224 Z" fill="white" opacity="0.5" />
          </g>

          {/* Greek columns near Odyssey Cove */}
          <g transform="translate(7, 245)" opacity="0.25">
            <rect x="0" y="0" width="1.8" height="12" fill="#8B7050" rx="0.3" />
            <rect x="5" y="0" width="1.8" height="12" fill="#8B7050" rx="0.3" />
            <rect x="-1" y="-1.5" width="9" height="2" fill="#8B7050" rx="0.3" />
            <rect x="-0.5" y="11" width="8" height="1.5" fill="#8B7050" rx="0.3" />
          </g>

          {/* Forest trees near Sherwood - bigger and more visible */}
          {[0,1,2,3,4,5,6].map(i => (
            <g key={`tree-${i}`} transform={`translate(${80 + i * 3}, ${360 + (i % 3) * 4})`} opacity="0.25">
              <line x1="0" y1="5" x2="0" y2="9" stroke="#5C3D1E" strokeWidth="0.4" />
              <path d={`M0 5 L-${1.5 + i%2} ${8} L${1.5 + i%2} ${8} Z`} fill="#2D5016" />
              <path d={`M0 3 L-${1.2 + i%2} ${5.5} L${1.2 + i%2} ${5.5} Z`} fill="#3A6B1E" />
              <path d={`M0 1 L-${0.8} ${3.5} L${0.8} ${3.5} Z`} fill="#4A8028" />
            </g>
          ))}

          {/* Deer near Sherwood Forest */}
          <g transform="translate(92, 378)" opacity="0.22">
            <ellipse cx="0" cy="0" rx="3.5" ry="2" fill="#8B6530" />
            <circle cx="-3.5" cy="-2" r="1.2" fill="#8B6530" />
            <line x1="-3" y1="-3.2" x2="-4" y2="-6" stroke="#6B4520" strokeWidth="0.4" />
            <line x1="-2.5" y1="-3.2" x2="-1.5" y2="-6.5" stroke="#6B4520" strokeWidth="0.4" />
            <line x1="-1.5" y1="2" x2="-1.5" y2="4" stroke="#6B4520" strokeWidth="0.3" />
            <line x1="1.5" y1="2" x2="1.5" y2="4" stroke="#6B4520" strokeWidth="0.3" />
          </g>

          {/* Lamppost near Narnia - taller, more visible */}
          <g transform="translate(10, 472)" opacity="0.30">
            <line x1="0" y1="0" x2="0" y2="14" stroke="#4A3224" strokeWidth="0.6" />
            <rect x="-2.5" y="-3" width="5" height="4" rx="0.8" fill="#4A3224" />
            <circle cx="0" cy="-1.5" r="1.5" fill="#FFC107" opacity="0.6" />
            {/* Light glow */}
            <circle cx="0" cy="-1.5" r="4" fill="#FFC107" opacity="0.08" />
          </g>

          {/* Snowflakes near Narnia */}
          {[0,1,2,3,4].map(i => (
            <text key={`snow-${i}`} x={14 + i * 5} y={490 + (i % 3) * 5} fontSize="2.5" fill="#6B8090" opacity="0.20">✦</text>
          ))}

          {/* Sea monster / serpent */}
          <g transform="translate(14, 605)" opacity="0.22">
            <path d="M0 0 Q4 -5 8 0 Q12 5 16 0 Q20 -5 24 0" fill="none" stroke="#4A6A5A" strokeWidth="0.7" />
            <circle cx="-1.5" cy="0" r="1.5" fill="#4A6A5A" />
            <circle cx="-0.5" cy="-0.5" r="0.4" fill="#8B6914" />
          </g>

          {/* X marks the spot near Treasure Island */}
          <g transform="translate(88, 560)" opacity="0.30">
            <line x1="-4" y1="-4" x2="4" y2="4" stroke="#CC2222" strokeWidth="0.8" />
            <line x1="4" y1="-4" x2="-4" y2="4" stroke="#CC2222" strokeWidth="0.8" />
          </g>

          {/* Waves at bottom */}
          <path d="M4 645 Q10 640 16 645 Q22 650 28 645 Q34 640 40 645" fill="none" stroke="#5C7A8A" strokeWidth="0.4" opacity="0.18" />
          <path d="M55 665 Q61 660 67 665 Q73 670 79 665 Q85 660 91 665" fill="none" stroke="#5C7A8A" strokeWidth="0.4" opacity="0.15" />
          <path d="M30 675 Q35 671 40 675 Q45 679 50 675" fill="none" stroke="#5C7A8A" strokeWidth="0.3" opacity="0.12" />

          {/* Scroll/book near The Tome of Legends */}
          <g transform="translate(11, 705)" opacity="0.25">
            <rect x="0" y="0" width="9" height="7" rx="1" fill="#A08050" />
            <rect x="0.5" y="0.5" width="8" height="6" rx="0.5" fill="#D2B88C" />
            <line x1="2" y1="2.5" x2="7" y2="2.5" stroke="#8B6914" strokeWidth="0.3" />
            <line x1="2" y1="4" x2="6" y2="4" stroke="#8B6914" strokeWidth="0.3" />
          </g>

          {/* Anchor near Docks */}
          <g transform="translate(14, 60)" opacity="0.20">
            <line x1="0" y1="-4" x2="0" y2="4" stroke="#4A5A6A" strokeWidth="0.6" />
            <path d="M-3 2 Q0 6 3 2" fill="none" stroke="#4A5A6A" strokeWidth="0.6" />
            <line x1="-2" y1="-2" x2="2" y2="-2" stroke="#4A5A6A" strokeWidth="0.5" />
            <circle cx="0" cy="-4" r="1" fill="none" stroke="#4A5A6A" strokeWidth="0.4" />
          </g>

          {/* ── Node connecting paths ── */}
          {RANKS.map((rank, i) => {
            if (i === 0) return null;
            const prev = NODE_POSITIONS[i - 1];
            const curr = NODE_POSITIONS[i];
            const y1 = 50 + (i - 1) * nodeSpacing;
            const y2 = 50 + i * nodeSpacing;
            const midY = (y1 + y2) / 2;

            // Meandering S-curve between nodes
            const cx1 = (prev.x + curr.x) / 2 + (i % 2 === 0 ? 12 : -12);
            const cx2 = (prev.x + curr.x) / 2 + (i % 2 === 0 ? -8 : 8);

            const isCompleted = currentRank > rank.rank;
            const isCurrent = currentRank === rank.rank;
            const pathColor = isCompleted || isCurrent ? NODE_COLORS[i] : "#C4A882";

            return (
              <g key={`path-${i}`}>
                {/* Path shadow */}
                <path
                  d={`M ${prev.x} ${y1 + 2} C ${cx1} ${midY - 15}, ${cx2} ${midY + 15}, ${curr.x} ${y2 + 2}`}
                  fill="none" stroke="#C4A882" strokeWidth="1.2" opacity="0.15"
                />
                {/* Main dotted/solid path */}
                <path
                  d={`M ${prev.x} ${y1} C ${cx1} ${midY - 15}, ${cx2} ${midY + 15}, ${curr.x} ${y2}`}
                  fill="none"
                  stroke={pathColor}
                  strokeWidth={isCompleted || isCurrent ? "0.8" : "0.5"}
                  strokeDasharray={isCompleted ? "none" : "2 2.5"}
                  opacity={isCompleted ? 0.5 : isCurrent ? 0.35 : 0.15}
                  strokeLinecap="round"
                />
              </g>
            );
          })}
        </svg>

        {/* Nodes */}
        <div className="relative" style={{ height: mapHeight - 40 }}>
          {RANKS.map((rank, i) => {
            const pos = NODE_POSITIONS[i];
            const isCompleted = currentRank > rank.rank;
            const isCurrent = currentRank === rank.rank;
            const isLocked = currentRank < rank.rank;
            const isNext = rank.rank === currentRank + 1;
            const size = isCurrent ? currentNodeSize : nodeSize;

            return (
              <div key={rank.rank}
                className="absolute flex items-center"
                style={{
                  left: `${pos.x}%`,
                  top: 30 + i * nodeSpacing,
                  transform: "translate(-50%, 0)",
                  zIndex: isCurrent ? 20 : 10
                }}>

                {/* Node cluster: circle + label */}
                <div className="flex flex-col items-center"
                  onClick={() => !isLocked && setSelectedNode(i)}
                  style={{ cursor: isLocked ? "default" : "pointer" }}>

                  {/* Glow ring for current */}
                  {isCurrent && (
                    <div className="absolute rounded-full pulse-glow"
                      style={{ width: size + 16, height: size + 16, top: -8, left: "50%", transform: "translateX(-50%)",
                        border: `3px solid ${NODE_COLORS[i]}40` }} />
                  )}

                  {/* Main circle */}
                  <div className={`rounded-full flex items-center justify-center transition-all ${isCurrent ? "float-anim" : ""}`}
                    style={{
                      width: size, height: size,
                      background: isLocked
                        ? "linear-gradient(135deg, #D2B48C, #C4A882)"
                        : `linear-gradient(135deg, ${NODE_COLORS[i]}dd, ${NODE_COLORS[i]})`,
                      border: `4px solid ${isLocked ? "#A0896C" : "#4A3224"}`,
                      boxShadow: isLocked ? "inset 0 2px 4px rgba(0,0,0,0.1)"
                        : isCurrent ? `0 6px 20px ${NODE_COLORS[i]}50, 0 3px 0 #4A3224`
                        : "0 3px 0 #4A3224, inset 0 2px 4px rgba(255,255,255,0.2)",
                      opacity: isLocked ? 0.5 : 1
                    }}
                    data-testid={`map-node-${rank.rank}`}>
                    {isLocked ? (
                      <Lock className="w-5 h-5" style={{ color: "#8B7355" }} />
                    ) : (
                      <span className={`${isCurrent ? "text-3xl" : "text-2xl"}`}>{rank.icon}</span>
                    )}
                  </div>

                  {/* Label below/beside */}
                  <div className="mt-1.5 text-center" style={{ maxWidth: 120 }}>
                    <p className={`font-heading font-bold leading-tight ${isCurrent ? "text-sm" : "text-xs"}`}
                      style={{ color: isLocked ? "#A0896C" : "#4A3224" }}>
                      {rank.location}
                    </p>
                    {isCurrent && (
                      <div className="mt-1 px-2 py-0.5 rounded-full inline-block"
                        style={{ background: NODE_COLORS[i], border: "2px solid #4A3224" }}>
                        <span className="text-[9px] font-heading font-bold text-white">YOU ARE HERE</span>
                      </div>
                    )}
                    {isCompleted && (
                      <span className="text-[10px] block" style={{ color: "#22C55E" }}>Explored ✓</span>
                    )}
                    {isNext && (
                      <span className="text-[9px] block" style={{ color: "#A0896C" }}>
                        {rank.threshold - stats.memorized} more to unlock
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Map footer */}
        <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: "1px dashed #C4A882" }}>
          <p className="font-heading text-[10px] font-bold" style={{ color: "#5C3D2E" }}>
            The Seven Seas of Learning
          </p>
          <p className="text-[9px]" style={{ color: "#8B6914" }}>
            {currentRank - 1} of {RANKS.length} explored
          </p>
        </div>
      </div>

      {/* Progress card */}
      <div className="tt-card p-4 mt-4" data-testid="map-progress">
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading text-sm font-semibold" style={{ color: "#4A3224" }}>
            {stats.memorized} items memorized
          </p>
          <span className="font-heading text-sm font-bold" style={{ color: NODE_COLORS[currentRank - 1] }}>
            {stats.rank?.name} {RANKS[currentRank - 1]?.icon}
          </span>
        </div>
        {stats.next_rank && (
          <>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: "#E8DDD0" }}>
              <div className="h-full rounded-full" style={{
                width: `${Math.min(((stats.tier3_plus || 0) / stats.next_rank.threshold) * 100, 100)}%`,
                background: `linear-gradient(90deg, ${NODE_COLORS[currentRank - 1]}, ${NODE_COLORS[Math.min(currentRank, 6)]})`
              }} />
            </div>
            <p className="text-xs mt-1" style={{ color: "#7A5946" }}>
              {stats.next_rank.threshold - (stats.tier3_plus || 0)} more to reach {stats.next_rank.name}
            </p>
          </>
        )}
      </div>

      {/* Lore Popup */}
      {selectedNode !== null && (
        <Dialog open={selectedNode !== null} onOpenChange={(o) => { if (!o) setSelectedNode(null); }}>
          <DialogContent className="sm:max-w-[400px] p-6 rounded-2xl" data-testid="map-lore-popup">
            <DialogTitle className="sr-only">{RANKS[selectedNode]?.location}</DialogTitle>
            <DialogDescription className="sr-only">Location lore</DialogDescription>
            <div className="text-center mb-3">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-2"
                style={{ background: `linear-gradient(135deg, ${NODE_COLORS[selectedNode]}dd, ${NODE_COLORS[selectedNode]})`,
                  border: "4px solid #4A3224", boxShadow: `0 4px 12px ${NODE_COLORS[selectedNode]}40` }}>
                <span className="text-4xl">{RANKS[selectedNode]?.icon}</span>
              </div>
            </div>
            <h3 className="font-heading text-lg font-bold text-center mb-1" style={{ color: "#4A3224" }}>
              {RANKS[selectedNode]?.location}
            </h3>
            <p className="font-heading text-sm font-semibold text-center mb-3" style={{ color: "#D97706" }}>
              Rank {RANKS[selectedNode]?.rank}: {RANKS[selectedNode]?.name}
            </p>
            <div className="parchment rounded-xl p-4 mb-3">
              <p className="text-sm leading-6" style={{ color: "#4A3224" }}>{MAP_LORE[selectedNode]}</p>
            </div>
            <p className="text-xs text-center" style={{ color: "#7A5946" }}>
              {RANKS[selectedNode]?.threshold === 0 ? "Starting rank" : `Unlock at ${RANKS[selectedNode]?.threshold} items memorized`}
            </p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
